import { isUserAdmin, isUserFree, isUserSubscribed } from '@/lib/user/utils'
import { User, Role, Permission } from '@/lib/types/user-types'

// Check if we're in development mode
export function isDevelopmentMode(): boolean {
    return process.env.NEXT_PUBLIC_VERCEL_ENV === 'development'
}

// Determine if a user ID belongs to a developer
export function isDeveloper(userId: string): boolean {
    return DEVELOPER_IDS.includes(userId)
} 

// Function to determine a user's role based on their attributes
export function determineUserRole(user: User): Role {
    // Admin check (you can customize this logic)
    if (isDeveloper(user.id) || isUserAdmin(user)) {
        return 'admin'
    }
    
    // Pro users get elevated permissions
    if (isUserSubscribed(user)) {
        return 'subscriber'
    }

    // Free users get limited permissions
    if (isUserFree(user)) {
        return 'user'
    }   
    
    // Default to guest for free users or unknown subscriptions
    return 'guest'
}

// Check if a user has a specific permission
export function hasPermission(user: User, permission: Permission): boolean {
    const role = determineUserRole(user)
    
    // Admin always has all permissions
    if (role === 'admin') {
        return true
    }
    
    // Check if the user's role has this permission
    return ROLE_PERMISSIONS[role].some(p => p === permission)
}




// Define base permissions first
const BASE_PERMISSIONS = {
    guest: [],
    
    user: [
        'update_conversation_title', 
        'bookmark_conversation_or_message',
        'upload_files', 
        'logged_in'
    ],

    subscriber: [
    
    ],
    
    admin: [
        'top_sidebar_button',
        'update_balance_or_subscription', 
        'is_admin',
    ]
} as const

// Define the role permissions mapping with dynamic inheritance
export const ROLE_PERMISSIONS = {
    // Guest gets only base guest permissions
    guest: [
        ...BASE_PERMISSIONS.guest,
    ],

    // User gets guest permissions plus user-specific permissions
    user: [
        ...BASE_PERMISSIONS.guest,
        ...BASE_PERMISSIONS.user,
    ],

    // Subscriber gets all user permissions plus additional subscriber-specific permissions
    subscriber: [
        ...BASE_PERMISSIONS.guest,
        ...BASE_PERMISSIONS.user,
        ...BASE_PERMISSIONS.subscriber,
    ],

    admin: [
        ...BASE_PERMISSIONS.guest,
        ...BASE_PERMISSIONS.user,
        ...BASE_PERMISSIONS.subscriber,
        ...BASE_PERMISSIONS.admin,
    ]
} as const


// List of developer user IDs
// We maintain this as a static list since you don't want to use a database
// You could replace this with a more dynamic approach if needed
export const DEVELOPER_IDS: string[] = [
    'e3fdfc29-0e4d-40c7-99db-84a9b7a0d197',
    '75c3b484-bda3-4681-b1d1-54bfebc2648b',
]